import { Alert, AlertTitle, Stack, Typography } from "@mui/material";
import {
  ButtonRow,
  DetailsList,
  DetailsListItem,
  Form,
  FormButton,
  FormStep,
  FormSubmit,
  InputField,
  Modal,
  SearchField,
  SearchMultipleField,
  useFormWizard,
  UseFormWizardStep,
  UseFormWizardStepProps,
  UseModalProps,
  useToast,
} from "@synota-io/synota-shared-ui";
import * as yup from "yup";
import { useCallback } from "react";
import { useWatch } from "react-hook-form";
import { getUrlTitle } from "../utils";
import { useNodeUrls } from "../../../api/hooks/useNodeUrls";
import { useNodeUsers } from "../../../api/hooks/useNodeUsers";
import { useNodeLinkUsers } from "../../../api/hooks/useNodeLinkUsers";
import { useParams } from "react-router-dom";

const schema = yup
  .object({
    node: yup
      .object({
        value: yup.string(),
        label: yup.string(),
        dashboard_url: yup.string(),
      })
      .nullable()
      .required("Node is required"),
    group_name: yup.string().required("Group name is required"),
    user_emails: yup
      .array()
      .of(
        yup.object({
          value: yup.string(),
          label: yup.string(),
          user_group_id: yup.number(),
          user_group_name: yup.string(),
          caption: yup.string(),
        }),
      )
      .min(1, "At least one user is required")
      .required("Users are required"),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

export const SelectNodeStep = ({
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { isLoading?: boolean }) => {
  const { nodeUrls, isPending } = useNodeUrls();

  return (
    <FormStep>
      <SearchField
        fullWidth
        isLoading={isPending}
        disablePortal={false}
        header="Select a Node"
        name="node"
        placeholder={isPending ? "Loading..." : "Select node"}
        control={control}
        options={nodeUrls.map((node) => ({
          value: node.uuid,
          label: getUrlTitle(node.dashboard_url),
          dashboard_url: node.dashboard_url,
        }))}
      />

      <InputField autoFocus header="Group Name" fullWidth control={control} name="group_name" />

      <ButtonRow>
        <FormSubmit
          fullWidth
          isLoading={isLoading}
          label="Next"
          disabled={control.getFieldState("node.value").invalid}
        />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

export const InformationStep = ({
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { isLoading?: boolean }) => {
  const values = useWatch({ control });

  const selectedNode = values.node;
  const server_uuid = selectedNode?.value || "";

  const { nodeUsers, isPending: isUsersPending } = useNodeUsers({ server_uuid });

  return (
    <FormStep>
      <SearchMultipleField
        fullWidth
        isLoading={isUsersPending || !server_uuid}
        disablePortal={false}
        header="Select Users"
        name="user_emails"
        placeholder={isUsersPending ? "Loading..." : "Select users"}
        control={control}
        disableCloseOnSelect
        options={nodeUsers.map((user) => ({
          value: user.email,
          label: user.public_name || user.email.split("@")[0],
          caption: user.email,
          ...user,
        }))}
      />

      <ButtonRow>
        <FormSubmit label="Review" isLoading={isLoading} fullWidth />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

const ConfirmationStep = ({
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { isLoading?: boolean }) => {
  const values = useWatch({ control });

  const userAlreadyInGroup = values.user_emails?.filter((u) => u.user_group_id) || [];

  return (
    <FormStep>
      <DetailsList dense sx={{ padding: 0 }}>
        <DetailsListItem xs title="Node">
          {values.node?.label || "-"}
        </DetailsListItem>
        <DetailsListItem xs title="Group Name">
          {values.group_name || "-"}
        </DetailsListItem>
        <DetailsListItem xs title="Users">
          {values.user_emails?.length
            ? values.user_emails.map((user: { label?: string; value?: string }) => (
                <Typography key={user.value} variant="body2">
                  {user.label} ({user.value})
                </Typography>
              ))
            : "-"}
        </DetailsListItem>
      </DetailsList>

      {userAlreadyInGroup.length ? (
        <Alert severity="warning">
          <AlertTitle>
            {userAlreadyInGroup.length > 1 ? "These users are" : "This user is"} going to be removed
            from their current group:
          </AlertTitle>
          <Stack>
            {userAlreadyInGroup.map((user) => (
              <Typography key={user.value} variant="subtitle1" color="text.primary">
                <span>{user.value} </span>
                <Typography component="span" color="text.secondary">
                  (currently on "{user.user_group_name}")
                </Typography>
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <ButtonRow>
        <FormSubmit autoFocus isLoading={isLoading} fullWidth label="Confirm and Submit" />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

const steps: UseFormWizardStep<FormValues>[] = [
  [SelectNodeStep, ["group_name", "node"]],
  [InformationStep, ["user_emails"]],
  [ConfirmationStep, []],
];

export const LinkUsersModalForm = ({
  onSuccess,
  ...props
}: UseModalProps & {
  onSuccess: (values: FormValues) => void;
}) => {
  const toast = useToast();
  const { nodeUrls } = useNodeUrls();
  const { linkUsers, isPending } = useNodeLinkUsers();
  const { linkNodeUuid } = useParams<{ linkNodeUuid: string }>();

  const onSubmit = useCallback(
    ({ node, group_name, user_emails }: FormValues) =>
      linkUsers(
        {
          server_uuid: node.value,
          group_name,
          user_emails: user_emails.map((u) => u.value || ""),
        },
        {
          onSuccess: () => {
            const key = "users-linked-" + Date.now();
            props.onClose();
            onSuccess({ node, group_name, user_emails });
            toast.success("Users linked successfully", {
              key,
              autoHideDuration: 5000,
            });
          },
        },
      ),
    [linkUsers, onSuccess, props, toast],
  );

  const preselectedNode = nodeUrls.find((node) => node.uuid === linkNodeUuid);

  const {
    render,
    previous,
    next,
    formState: { errors },
  } = useFormWizard<FormValues>({
    onSubmit,
    defaultValues: {
      node: preselectedNode
        ? {
            value: preselectedNode.uuid,
            label: getUrlTitle(preselectedNode.dashboard_url),
            ...preselectedNode,
          }
        : null,
      group_name: "",
      user_emails: [],
    } as unknown as FormValues,
    schema,
    steps,
  });

  return (
    <Modal preventCloseOnBackdropClick title="Link Node Users" maxWidth="sm" fullWidth {...props}>
      <Form onSubmit={next} width="100%">
        {render({ isLoading: isPending, previous })}
        {errors.root && (
          <Typography color="error" variant="body1">
            {errors.root.message}
          </Typography>
        )}
      </Form>
    </Modal>
  );
};
